import React, { useState, useEffect, useRef, useContext } from 'react';
// import { useIntl } from 'react-intl';
import Link from 'next/link';
import clsx from 'clsx';

import styles from './masthead.module.scss';

import { assetsPath, NAV_ELEMENTS, SHOPPERS_URL, PHARMA_URL, PHARMA_LOGO, SDM_LOGO } from 'Data/constants';
import Icons from './icons';
import { NavContext } from '../../context/nav-context';
import SearchBarMobile from './search-bar-mobile/search-bar-mobile';
import SearchBar from './search-bar/search-bar';
import { isPharmaprixBanner } from '../../utils/utils';
import { useWindowSize, useDetermineEnvironment, useDetermineBanner } from '../../hooks';
import { formatLink } from '@/utils';
import { CondensedMasthead } from '../shared/condensed-masthead/condensed-masthead';
import Image from 'next/image';
import { useIntl } from 'react-intl';

export const getPharmaPrixLogo = (isPharmaprix: boolean) => (isPharmaprix ? PHARMA_LOGO : SDM_LOGO);
export const getURL = (isPharmaprix: boolean) => (isPharmaprix ? PHARMA_URL : SHOPPERS_URL);
export const getLogoStyle = (isPharmaprix: boolean) => (isPharmaprix ? styles.pharmaprixLogo : styles.sdmLogo);
export const getRightMenuPharmaPrixStyle = (isPharmaprix: boolean) => isPharmaprix && styles.rightMenuPharmaprix;

const Masthead = ({ showOnlyLogo }: { showOnlyLogo?: boolean }) => {
  const _showOnlyLogo: boolean = !!showOnlyLogo;
  const searchBarRef: any = useRef();
  const [isPharmaprix, setIsPharmaprix] = useState(false);
  const {
    nav: {
      ['isMobileNavOpen']: [isMobileNavOpen, setIsMobileNavOpen],
    },
    user: {
      ['bagCount']: [bagCount],
    },
  } = useContext(NavContext);

  useEffect(() => {
    setIsPharmaprix(isPharmaprixBanner() || false);
    if (searchBarRef.current) searchBarRef.current.focus();
  }, []);

  const { formatMessage } = useIntl();
  const isPhxBanner = useDetermineBanner();
  const env = useDetermineEnvironment();

  const { isDesktop, isMobile } = useWindowSize({ isModernized: true });
  const src = getPharmaPrixLogo(isPharmaprix);
  const href = getURL(isPharmaprix);
  const toggleMobileNav = () => setIsMobileNavOpen(!isMobileNavOpen);

  if (typeof window !== 'undefined') {
    //chek if this line is needed
    if (isMobileNavOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }
  return _showOnlyLogo ? (
    <CondensedMasthead isPharmaprix={isPharmaprix} altText={formatMessage({ id: 'shoppersDrugMart' })} />
  ) : isMobile ? (
    <div className={clsx(styles.masthead, styles.mobile)} role="banner">
      <div className={clsx(styles.wrapper, styles.mobileWrapper)}>
        <div className={styles.leftHeader}>
          <a href="#main-content" className={styles.skipToMain} data-testid="skipToMainHref">
            {formatMessage({ id: 'skipToMain' })}
          </a>

          <div className={styles.mobileMenu}>
            <Link
              href={`#${NAV_ELEMENTS.MOBILE_MENU}`}
              id={NAV_ELEMENTS.MOBILE_MENU}
              onClick={toggleMobileNav}
              data-testid={NAV_ELEMENTS.MOBILE_MENU}
            >
              <Image
                src={`${assetsPath}/site-media/hamburger-nav.svg`}
                alt={'hamburger navigation'}
                aria-label={'hamburger navigation'}
                width={0}
                height={0}
                style={{ width: '100%', height: 'auto' }}
              />
            </Link>
          </div>
        </div>
        <div className={styles.centerHeader} style={{ justifyContent: 'center' }}>
          <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ width: '100%', height: '100%', position: 'relative' }}
          >
            <Image
              src={src}
              alt={formatMessage({ id: 'shoppersDrugMart' })}
              aria-label={formatMessage({ id: 'shoppersDrugMart' })}
              width={0}
              height={0}
              style={{ width: '100%', height: 'auto' }}
              className={clsx(getLogoStyle(isPharmaprix), styles.mobile)}
            />
          </Link>
        </div>
        <div className={clsx(styles.rightHeader, styles.mobileHeader)}>
          <Link href={formatLink('/shoppingbag', isPhxBanner, env)}>
            <span className={styles.shoppingBag} aria-label={formatMessage({ id: 'bag' })}>
              <Image
                src={`${assetsPath}/site-media/shopping-bag.svg`}
                alt={formatMessage({ id: 'bag' })}
                aria-label={formatMessage({ id: 'bag' })}
                width={0}
                height={0}
                style={{ width: '100%', height: 'auto' }}
              />
              <span id="js-cart-item-count-nav-sub" className={styles.miniBag}>
                {bagCount}
              </span>
            </span>
          </Link>
        </div>
      </div>
      <div className={styles.searchDropdownContainer}>
        <SearchBarMobile searchBarRef={searchBarRef} />
      </div>
    </div>
  ) : (
    <div className={clsx(styles.masthead, !isDesktop && styles.mobile)} role="banner">
      <div className={styles.wrapper}>
        <a href="#main-content" className={styles.skipToMain} data-testid="skipToMainHref">
          {formatMessage({ id: 'skipToMain' })}
        </a>
        {isDesktop ? null : (
          <div className={styles.leftHeader}>
            <div className={styles.mobileMenu}>
              <Link
                href={`#${NAV_ELEMENTS.MOBILE_MENU}`}
                id={NAV_ELEMENTS.MOBILE_MENU}
                onClick={toggleMobileNav}
                data-testid={NAV_ELEMENTS.MOBILE_MENU}
              >
                <Image
                  src={`${assetsPath}/site-media/hamburger-nav.svg`}
                  alt={'hamburger navigation'}
                  aria-label={'hamburger navigation'}
                  width={0}
                  height={0}
                  style={{ width: '100%', height: 'auto' }}
                />
              </Link>
            </div>
          </div>
        )}
        <div className={clsx(styles.centerHeader, isDesktop ? styles.showLogoAtStart : styles.showLogoJustified)}>
          <Link href={href} rel="noopener noreferrer">
            <Image
              src={src}
              alt={formatMessage({ id: 'shoppersDrugMart' })}
              className={clsx(getLogoStyle(isPharmaprix), !isDesktop && styles.mobile)}
              aria-label={formatMessage({ id: 'shoppersDrugMart' })}
              width={0}
              height={0}
              style={{ height: '100%', width: 'auto' }}
            />
          </Link>
        </div>
        {isDesktop ? (
          <>
            <SearchBar />
            <div className={clsx(styles.rightMenu, getRightMenuPharmaPrixStyle(isPharmaprix))}>
              <Icons />
            </div>
          </>
        ) : (
          <div className={styles.rightHeader}>
            <Link href={formatLink('/shoppingbag', isPhxBanner, env)}>
              <span className={styles.shoppingBag} aria-label={formatMessage({ id: 'bag' })}>
                <Image
                  src={`${assetsPath}/site-media/shopping-bag.svg`}
                  alt={formatMessage({ id: 'bag' })}
                  aria-label={formatMessage({ id: 'bag' })}
                  width={0}
                  height={0}
                  style={{ width: '100%', height: 'auto' }}
                />
                <span id="js-cart-item-count-nav-sub" className={styles.miniBag}>
                  {bagCount}
                </span>
              </span>
            </Link>
          </div>
        )}
      </div>
      {isDesktop ? null : (
        <div className={styles.searchDropdownContainer}>
          <SearchBarMobile searchBarRef={searchBarRef} />
        </div>
      )}
    </div>
  );
};

export default Masthead;
